/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { addDoc, collection, getDocs, query, where, doc, updateDoc, deleteDoc, Timestamp, getDoc } from 'firebase/firestore';
import { MailTemplate } from 'src/@types/event';
import { Step } from 'src/guards/admin/types';

import { DB } from 'src/contexts/FirebaseContext';

export interface UserRequest {
  uid: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  about?: string;
  photoURL?: string;
  isNewUser?: boolean;
  stripeConnect?: StripeConnectAccount;
}

export interface TicketBasicRequest {
  id: string;
  startDate?: Date;
  endDate?: Date;
}

export interface StripeConnectAccount {
  accountId: string;
  status: string;
}





type UpdateableMailMailTemplateFields = Omit<MailTemplate, 'id' | 'userId'>;

interface UpdateMailTemplatePayload {
  id: string;
  userId: string;
  updates: Partial<UpdateableMailMailTemplateFields>;
}


// Define a service using a base URL and expected endpoints
export const UsersApi = createApi({
  reducerPath: 'UsersApi',
  baseQuery: fakeBaseQuery(),
  refetchOnReconnect: true,
  tagTypes: ['User', 'MailTemplate'],
  endpoints: (builder) => ({
    //events
    fetchUserByrole: builder.query<any[], any>({
      async queryFn(role) {
        const constraints = [];
        constraints.push(where('role', '==', role));
        let eventsRef = collection(DB, 'users');
        const qEvents = query(eventsRef, ...constraints); 

        let promotersData: any[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { data: promotersData };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ uid }) => ({ type: 'User' as const, uid })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    fetchUsersByOrg: builder.query<any[], any>({
      async queryFn(orgid) {
        const constraints = [];
        constraints.push(where('orgId', '==', orgid));
        let eventsRef = collection(DB, 'users');
        const qEvents = query(eventsRef, ...constraints); 

        let promotersData: any[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { data: promotersData };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ uid }) => ({ type: 'User' as const, uid })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    addDjInvitation: builder.mutation<any, any>({
      async queryFn(invitationData) {
        let docId = '';
        let invitationDocRef = collection(DB, 'users', invitationData.uid, 'djInvitation');

        const docRef = await addDoc(invitationDocRef, invitationData);
        docId = docRef.id;
        return {
          data: { ...invitationData, invitationId: docId },
        };
      },
    }),

    fetchMailTemplates: builder.query<MailTemplate[], string>({
      async queryFn(userId) {
        const mailTemplatesRef = collection(DB, 'users', userId, 'mailTemplates');
        const querySnapshot = await getDocs(mailTemplatesRef);
        const mailCampaigns: MailTemplate[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<MailTemplate, 'id'>),
        }));
        return { data: mailCampaigns };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MailTemplate' as const, id })),
              { type: 'MailTemplate', id: 'LIST' },
            ]
          : [{ type: 'MailTemplate', id: 'LIST' }],
    }),
    fetchMailTemplate: builder.query<MailTemplate, { userId: string; templateId: string }>({
      async queryFn({ userId, templateId }) {
        try {
          const mailTemplateRef = doc(DB, 'users', userId, 'mailTemplates', templateId);
          const docSnap = await getDoc(mailTemplateRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data() as Omit<MailTemplate, 'id'>;
            return { data: { id: docSnap.id, ...data } };
          } else {
            return { error: { message: 'Mail campaign not found' } };
          }
        } catch (error) {
          return { error: { message: 'Failed to fetch mail campaign', originalError: error } };
        }
      },
      providesTags: (result, error, arg) => [{ type: 'MailTemplate', id: arg.templateId }],
    }),

    addMailTemplate: builder.mutation<MailTemplate, Omit<MailTemplate, 'id'>>({
      async queryFn(mailTemplateData) {
        const mailTemplatesRef = collection(DB, 'users', mailTemplateData.userId, 'mailTemplates');
        const docRef = await addDoc(mailTemplatesRef, mailTemplateData);
        return { data: { ...mailTemplateData, id: docRef.id } };
      },
      invalidatesTags: [{ type: 'MailTemplate', id: 'LIST' }],
    }),

    updateMailTemplate: builder.mutation<boolean, UpdateMailTemplatePayload>({
      async queryFn({ id, userId, updates }) {
        try {
          const mailTemplateRef = doc(DB, 'users', userId, 'mailTemplates', id);
          await updateDoc(mailTemplateRef, updates);
          return { data: true }; // Return a boolean to indicate success
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: 'MailTemplate', id: arg.id }],
    }),

    deleteMailTempalte: builder.mutation<void, { userId: string; tempalteId: string }>({
      async queryFn({ userId, tempalteId }) {
        const mailTemplateRef = doc(DB, 'users', userId, 'mailTemplates', tempalteId);
        await deleteDoc(mailTemplateRef);
        return { data: undefined };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'MailTemplate', id: arg.tempalteId }],
    }),

    updateOnboardingSteps: builder.mutation<boolean, { uid: string; onBoardingSteps: Step[] }>({
      async queryFn({ uid, onBoardingSteps }) {
        try {
          const userRef = doc(DB, 'users', uid);
          await updateDoc(userRef, { onBoardingSteps });
          return { data: true };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', uid: arg.uid }],
    }),
  }),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFetchUserByroleQuery,
  useFetchUsersByOrgQuery,
  useAddDjInvitationMutation,
  useFetchMailTemplateQuery,
  useFetchMailTemplatesQuery,
  useAddMailTemplateMutation,
  useUpdateMailTemplateMutation,
  useUpdateOnboardingStepsMutation,
 } = UsersApi;
